<template>
  <transition name="fade">
    <div v-if="props.show" class="select-auto">
      <div
        class="absolute w-screen h-screen bg-black opacity-70 inset-0 z-10"
        @click.stop="onModalToggle"
      />
      <div
        class="absolute inset-0 w-screen h-screen flex items-center justify-center z-10"
      >
        <div
          class="w-[95%] sm:w-[90%] max-w-[600px] p-4 sm:p-8 relative mx-auto my-auto shadow-lg bg-white rounded-[10px] z-10"
        >
          <!-- Close Button -->
          <button
            aria-label="Close modal"
            class="absolute top-2 right-2 sm:top-4 sm:right-4 w-12 h-12 sm:w-8 sm:h-8 flex items-center justify-center text-black hover:text-gray-600 transition-colors z-50"
            @click="onModalToggle"
          >
            <svg
              class="w-7 h-7 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              stroke-width="2.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div class="text-center pt-4 sm:pt-2">
            <h2
              class="text-2xl sm:text-[32px] font-bold text-black mb-2 sm:mb-3 px-8"
            >
              Colors Updated
            </h2>
            <p
              class="text-xs sm:text-sm font-semibold text-black mb-6 sm:mb-8 px-4"
            >
              Some colors in your design have been discontinued and replaced:
            </p>

            <div
              v-if="colorEntries.length"
              class="flex flex-col gap-4 mb-6 sm:mb-8 px-4 sm:px-8"
            >
              <div
                v-for="(entry, index) in colorEntries"
                :key="index"
                class="flex items-center justify-center gap-3"
              >
                <div class="flex items-center gap-2 min-w-[120px] justify-end">
                  <span class="text-sm sm:text-base text-black">{{
                    entry.from
                  }}</span>
                  <img
                    v-if="entry.fromSample"
                    :src="entry.fromSample"
                    :alt="entry.from"
                    class="w-8 h-8 rounded-full object-cover border border-gray-200"
                  />
                </div>
                <svg
                  class="w-4 h-4 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
                <div class="flex items-center gap-2 min-w-[120px]">
                  <img
                    v-if="entry.toSample"
                    :src="entry.toSample"
                    :alt="entry.to"
                    class="w-8 h-8 rounded-full object-cover border border-gray-200"
                  />
                  <span class="text-sm sm:text-base text-black font-semibold">{{
                    entry.to
                  }}</span>
                </div>
              </div>
            </div>

            <button
              class="bg-[#313358] text-white rounded-full px-8 py-3 font-semibold hover:bg-[#252842] transition-colors min-h-[44px] min-w-[120px]"
              @click="onModalToggle"
            >
              Got It
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ModalType, useModalStore } from "~/stores/modal";
import COLOR_CHOICES from "~/composables/shapes/colors";

const modalStore = useModalStore();

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["modalToggle"]);

const colorSampleByLabel = computed(() => {
  const map = {};
  for (const c of COLOR_CHOICES) {
    map[c.label] = c.image || c.sample;
  }
  return map;
});

const colorEntries = computed(() => {
  const options = modalStore.getModalOptions(ModalType.ColorRemap);
  const remapped = options?.remappedColors || [];
  return remapped.map((entry) => ({
    ...entry,
    fromSample: colorSampleByLabel.value[entry.from],
    toSample: colorSampleByLabel.value[entry.to],
  }));
});

const onModalToggle = () => {
  emit("modalToggle");
};
</script>

<style scoped>
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease-out;
}
</style>
